import { useCallback } from 'react'
import cn from 'classnames'
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'

import CollapsableContent from 'components/CollapsableContent'

import ReorderableAndHideableComponentsActions from './Actions'
import EmptyMessage from './EmptyMessage'
import ReorderableAndHideableComponent from './ReorderableAndHideableComponent'
import { useReorderingAndVisibility } from './hooks'

function SortableReorderableAndHideableComponent(props) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: `${props.index}` })

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
    >
      <ReorderableAndHideableComponent {...props} />
    </div>
  )
}

export default function ReorderableAndHideableComponents({
  className,
  collection,
  itemComponent,
  hideable = true,
  isInEditModeClassName = 'isInEditMode',
  initialOrder,
  itemNoun = 'item',
  itemNounPlural = `${itemNoun}s`,
  itemProps,
  hideTrigger = `– Hide extra ${itemNounPlural}`,
  showTrigger = `+ Show all ${itemNounPlural}`,
  onToggle,
  emptyMessage = `No visible ${itemNounPlural}`,
  namespace = 'reorderable_and_hideable_components',
  reorderable = true,
}) {
  const {
    doEnterEditMode,
    doExitEditMode,
    doHideItem,
    doReorderItems,
    doShowItem,
    isInEditMode,
    items,
  } = useReorderingAndVisibility(namespace, collection, initialOrder)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )
  const handleDragEnd = useCallback(
    ({ active, over }) => {
      if (!over || active.id === over.id) return
      doReorderItems({ oldIndex: Number(active.id), newIndex: Number(over.id) })
    },
    [doReorderItems]
  )
  const { visibleItems, hiddenItems } = items.reduce(
    (result, { index, key, visible }) => {
      const addTo =
        visible || hideable === false ? result.visibleItems : result.hiddenItems
      const Wrapper = reorderable
        ? SortableReorderableAndHideableComponent
        : ReorderableAndHideableComponent
      const computedProps =
        typeof itemProps === 'function'
          ? itemProps(key, index, visible)
          : itemProps
      addTo.push(
        <Wrapper
          collection={`${collection}:${visible ? 'visible' : 'hidden'}`}
          key={key}
          index={index}
          isInEditMode={isInEditMode}
          isVisible={visible}
          itemComponent={itemComponent}
          itemKey={key}
          itemProps={computedProps}
          onHideItem={hideable && doHideItem}
          onShowItem={hideable && doShowItem}
        />
      )
      return result
    },
    { visibleItems: [], hiddenItems: [] }
  )
  const areMoreItems = hiddenItems.length > 0
  const visibleIds = visibleItems.map(({ props: { index } }) => `${index}`)
  const hiddenIds = hiddenItems.map(({ props: { index } }) => `${index}`)
  const visibleContent =
    visibleItems.length > 0 ? (
      visibleItems
    ) : (
      <EmptyMessage>{emptyMessage}</EmptyMessage>
    )
  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div
        className={cn('ReorderableAndHideableComponents', className, {
          isInEditMode,
          [isInEditModeClassName]: isInEditMode,
        })}
      >
        {reorderable ? (
          <SortableContext
            items={visibleIds}
            strategy={verticalListSortingStrategy}
          >
            {visibleContent}
          </SortableContext>
        ) : (
          visibleContent
        )}
        <ReorderableAndHideableComponentsActions
          isInEditMode={isInEditMode}
          onDone={doExitEditMode}
          onEdit={doEnterEditMode}
          noMoreFields={!areMoreItems}
        />
        {areMoreItems && (
          <CollapsableContent
            hideTrigger={!isInEditMode && hideTrigger}
            showTrigger={!isInEditMode && showTrigger}
            shown={isInEditMode === true ? true : null}
            onToggle={onToggle}
          >
            {reorderable ? (
              <SortableContext
                items={hiddenIds}
                strategy={verticalListSortingStrategy}
              >
                {hiddenItems}
              </SortableContext>
            ) : (
              hiddenItems
            )}
          </CollapsableContent>
        )}
      </div>
    </DndContext>
  )
}
